import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@cygnusaiinternal/cygnus-common/index.css";
import "./Dashboard.css";
import * as Icon from "react-bootstrap-icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CygnusAppNavigator,
  type NavigatorBottomAction,
  type NavigatorChildItem,
  type NavigatorItem,
} from "@cygnusaiinternal/cygnus-common";

import { setToLS } from "../../utils/storage.js";
import { getDeepLink, logout, hasFeature as checkFeatureAccess } from "utils/index.js";
import DiffToolMainClientGridPage from "../client_reconciliation/diff_tool_main_client_grid_page/DiffToolMainClientGridPage.js";
import ClientsGrid from "../../pages/client_onboarding/ClientsGrid/ClientsGrid";
import ClientApprovalGrid from "../../pages/client_onboarding/ClientApprovalGrid/ClientApprovalGrid";
import ClientDataMapping from "../../pages/client_onboarding/ClientDataMapping/ClientDataMapping";
import EngagementDashboard from "../../pages/cygnus_dashboards/Engagement_Dashboard/EngagementDashboard";
import EngagementDashboardOld from "../../pages/cygnus_dashboards/Engagement_Dashboard_Old/EngagementDashboardOld";
import ClientDashboard from "../../pages/cygnus_dashboards/Client_Dashboard/ClientDashboard";
import MyTemplatesPage from "../engagement_letter/my_templates_page/MyTemplatesPage.js";
import ApprovalDetailsPage from "../../pages/engagement_letter/ApprovalDetailsPage/ApprovalDetailsPage";
import ContentBlocksPage from "../engagement_letter/ContentBlocksPage/ContentBlocksPage";
import RulesPage from "../engagement_letter/RulesPage/RulesPage";
import WizardFormConfig from "../engagement_letter/wizard_form_builder/my_wizard_forms/WizardFormConfig.js";
import ApprovalConfig from "../firm_setup/ApprovalConfig/ApprovalConfig";
import COIRegistry from "../firm_setup/COIRegistry/COIRegistry";
import ConflictCheckResults from "../firm_setup/ConflictCheckResults/ConflictCheckResults";
import OnboardingFormConfig from "../../pages/client_onboarding/OnboardingFormConfig/OnboardingFormConfig";
import IntakeFormsGrid from "../../pages/client_onboarding/IntakeFormsGrid/IntakeFormsGrid";
import EngagementFormsGrid from "../../pages/engagement_letter/EngagementFormsGrid/EngagementFormsGrid";
import EngagementFormConfig from "../../pages/engagement_letter/EngagementFormConfig/EngagementFormConfig";
import MDMFieldsConfig from "../../pages/client_onboarding/MDMFieldsConfig/MDMFieldsConfig";
import FieldMappings from "../../pages/firm_setup/FieldMappings/FieldMappings";
import EngagementLetterDetailsPage from "../../pages/engagement_letter/EngagementLetterDetailsPage/EngagementLetterDetailsPage";
import CygnusGptDashboard from "../../pages/cygnus_gpt/CygnusGptDashboard/CygnusGptDashboard";
import { useTheme } from "../../themes/useTheme";
import { useChartTheme } from "../../themes/useChartTheme";
import chart_themes from "../../themes/chartThemes.json";
import { applyCssVars } from "../../themes/useTheme";
import ThemeSelector from "../../themes/ThemeSelectorComponent/ThemeSelector";
import FirmAdminPage from "../../pages/FirmAdmin/FirmAdminPage";

type DashboardNavigationOptions = Record<string, unknown>;
type PageOptions = Record<string, unknown>;
type DashboardDeepLink = { tab?: string; recordId?: string; action?: string };

type ThemeBroadcastPayload = {
  catalog?: Record<string, unknown> | null;
  chartCatalog?: Record<string, unknown> | null;
  active?: unknown;
  activeChart?: unknown;
};

const DEFAULT_ACTIVE_KEY = "clients";

const renderPage = (Component: React.ComponentType<any>, props?: Record<string, unknown>): React.ReactNode =>
  React.createElement(Component, props);

const Dashboard: React.FC = () => {
  const firmImg = localStorage.getItem("firmImg");
  const userType = localStorage.getItem("usertype");
  const firmName = localStorage.getItem("firmname") ?? undefined;
  const fullName = `${localStorage.getItem("firstname") || ""} ${localStorage.getItem("lastname") || ""}`.trim();
  const userName= fullName || localStorage.getItem("username") || localStorage.getItem("user") || "";
  const navigate = useNavigate();
  const location = useLocation();

  const [activeKey, setActiveKey] = useState<string>(
    localStorage.getItem("defaultActiveKey") || DEFAULT_ACTIVE_KEY
  );
  const [pageOptions, setPageOptions] = useState<PageOptions>({});
  const [deepLink, setDeepLink] = useState<DashboardDeepLink>();

  const { setMode } = useTheme();
  const { setChartMode } = useChartTheme();

  const hasFeature = useCallback(
    (featureCode: string): boolean => checkFeatureAccess(featureCode),
    []
  );

  const saveParamToApi = useCallback(async (parameterName: string, value: unknown): Promise<void> => {
    const firmId = localStorage.getItem("firmId");
    if (!firmId || !value) return;

    try {
      await fetch(`${process.env.REACT_APP_BASE_URI}/workflow/personalization/set_params/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firm_id: firmId,
          parameter_name: parameterName,
          parameter_value_1: JSON.stringify(value),
        }),
      });
    } catch (err) {
      console.error(`Failed to save ${parameterName}:`, err);
    }
  }, []);

  const didHydrateThemeParams = useRef(false);

  useEffect(() => {
    if (didHydrateThemeParams.current) return;
    didHydrateThemeParams.current = true;

    const firmId = localStorage.getItem("firmId");
    if (!firmId) return;

    const fetchParam = async (parameterName: string): Promise<unknown> => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URI}/workflow/personalization/get_params/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ firm_id: firmId, parameter_name: parameterName }),
        });
        const json = await res.json();
        const row = json?.data?.[0]?.params?.[0]?.parameter_value_1;
        return row ? JSON.parse(row) : null;
      } catch (err) {
        console.error("Theme fetch failed:", err);
        return null;
      }
    };

    void (async () => {
      const [active, activeChart, allThemes] = await Promise.all([
        fetchParam("activeTheme"),
        fetchParam("activeChartTheme"),
        fetchParam("themes"),
      ]);

      const catalog = (allThemes as Record<string, unknown> | null) || {};
      const chartCatalog = chart_themes?.data || {};

      if (Object.keys(catalog).length) {
        setToLS("all-themes", catalog);
      }

      if (Object.keys(chartCatalog).length) {
        setToLS("chart-themes", chartCatalog);
      }

      if (active) {
        setToLS("theme", active);
        setMode(active);
        applyCssVars(active);
      }

      if (activeChart) {
        setToLS("chart-theme", activeChart);
        setChartMode(activeChart);
      }

      window.dispatchEvent(new CustomEvent("cygnus:themes:catalog", { detail: { catalog, chartCatalog } }));
      window.dispatchEvent(new CustomEvent("cygnus:themes:active", { detail: { theme: active, chartTheme: activeChart } }));
    })();
    // Theme hydration should run once per dashboard mount. Theme selection changes are saved by ThemeSelector.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handlePopState = () => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    
    if (!tab) return; 
    setActiveKey(tab);
    localStorage.setItem("defaultActiveKey", tab);
  };

  window.addEventListener("popstate", handlePopState);

  return () => {
    window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleNavigate = useCallback(
    (nextKey: string, options?: DashboardNavigationOptions): void => {
      localStorage.setItem("defaultActiveKey", nextKey);
      setActiveKey(nextKey);

      setPageOptions((current) => ({
        ...current,
        [nextKey]:
          options?.[`${nextKey}_option`] ??
          options?.mapping_option ??
          options?.onboarding_form_config_option ??
          options?.engagement_form_config_option ??
          options?.wizard_config_option ??
          undefined,
      }));

      if (location.pathname !== "/dashboard") {
        navigate("/dashboard");
      } else {
        navigate(`/dashboard?tab=${nextKey}`);
      }
    },
    [location.pathname, navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get("tab");
    if (tabFromUrl) {
      setActiveKey(tabFromUrl);
      localStorage.setItem("defaultActiveKey", tabFromUrl);
    }
  }, []);

  useEffect(() => {
    const dl = getDeepLink();
    if (!dl) return;

    if (dl.tab) {
      setActiveKey(dl.tab);
      localStorage.setItem("defaultActiveKey", dl.tab);
    }
    setDeepLink(dl);
  }, []);

  const redirectToDatahub = useCallback((): void => {
    const appEnv = process.env.REACT_APP_ENV;
    const url =
      appEnv === "uat"
        ? process.env.REACT_APP_DATAHUB_UAT_URL
        : appEnv === "dev"
          ? process.env.REACT_APP_DATAHUB_DEV_URL
          : process.env.REACT_APP_DATAHUB_PROD_URL;

    if (url) {
      window.open(url, "_blank");
    } else {
      console.error("invalid environment configuration");
    }
  }, []);

  const items = useMemo<NavigatorItem[]>(() => {
    const nav: NavigatorItem[] = [];

    if (hasFeature("CGPT")) {
      nav.push({ key: "chatgpt", label: "AI Companion", actionType: "internal", icon: <Icon.ChatSquareTextFill /> });
    }

    const dashboardItems = [
      hasFeature("CLIENT_DSH")
        ? { key: "client_dashboard", label: "Client Dashboard", actionType: "internal" as const, icon: <Icon.Grid1x2Fill /> }
        : null,
      hasFeature("EL_DSH2")
        ? { key: "el_dashboard", label: "EL Dashboard", actionType: "internal" as const, icon: <Icon.Grid1x2Fill /> }
        : null,
      hasFeature("EL_DSH")
        ? { key: "el_dashboard_old", label: "EL Dashboard", actionType: "internal" as const, icon: <Icon.Grid1x2Fill /> }
        : null,
    ].filter(Boolean) as NavigatorChildItem[];

    if (dashboardItems.length > 0) {
      nav.push({
        key: "dashboards",
        label: "Dashboards",
        icon: <Icon.Grid1x2Fill />,
        childrenOpenMode: "inline",
        children: [{ key: "dashboards-general", label: "General", items: dashboardItems }],
      });
    }

    if (hasFeature("CLT_MAIN")) {
      nav.push({ key: "clients", label: "Clients", actionType: "internal", icon: <Icon.FilePersonFill /> });
    }

    if (hasFeature("CLT_APRV")) {
      nav.push({ key: "client_approval", label: "Client Approval", actionType: "internal", icon: <Icon.PersonFillCheck /> });
    }

    if (hasFeature("EL_CRT")) {
      nav.push({ key: "client_details", label: "Engagement Letters", actionType: "internal", icon: <Icon.FileEarmarkRichtextFill /> });
    }

    if (hasFeature("EL_APRV")) {
      nav.push({ key: "approval_details", label: "EL Approval", actionType: "internal", icon: <Icon.PersonFillCheck /> });
    }

    const configurationItems = [
      hasFeature("CFG_FLDS")
        ? { key: "configure_fields", label: "Configure Fields", actionType: "internal" as const, icon: <Icon.Grid1x2Fill /> }
        : null,
      hasFeature("FLD_MAP")
        ? { key: "field_mapping", label: "Field Mapping", actionType: "custom" as const, icon: <Icon.Bezier />, onClick: () => handleNavigate("field_mapping") }
        : null,
      hasFeature("INTK_TMPL")
        ? { key: "onboarding_forms", label: "Intake Templates", actionType: "custom" as const, icon: <Icon.PostcardFill />, onClick: () => handleNavigate("onboarding_forms") }
        : null,
      hasFeature("APR_CFG")
        ? { key: "approval_config", label: "Approval Configuration", actionType: "custom" as const, icon: <Icon.GearFill />, onClick: () => handleNavigate("approval_config") }
        : null,
      hasFeature("SYS_MAP")
        ? { key: "mappings", label: "Mappings", actionType: "custom" as const, icon: <Icon.GearFill />, onClick: () => handleNavigate("mappings") }
        : null,
      hasFeature("COI_REG")
        ? { key: "coi_registry", label: "COI Registry", actionType: "custom" as const, icon: <Icon.ShieldFillCheck />, onClick: () => handleNavigate("coi_registry") }
        : null,
    ].filter(Boolean) as NavigatorChildItem[];

    if (configurationItems.length > 0) {
      nav.push({
        key: "configurations",
        label: "Configurations",
        icon: <Icon.GearWideConnected />,
        childrenOpenMode: "inline",
        children: [{ key: "configurations-general", label: "General", items: configurationItems }],
      });
    }

    const engagementItems = [
      hasFeature("EL_SET_TMPL")
        ? { key: "templates", label: "Templates", actionType: "custom" as const, icon: <Icon.FileEarmarkRuledFill />, onClick: () => handleNavigate("templates") }
        : null,
      hasFeature("EL_SET_WZFRM")
        ? { key: "engagement_forms", label: "Wizard Forms", actionType: "custom" as const, icon: <Icon.PostcardFill />, onClick: () => handleNavigate("engagement_forms") }
        : null,
      hasFeature("EL_SET_CNTBLK")
        ? { key: "content_blocks", label: "Content Blocks", actionType: "custom" as const, icon: <Icon.Paragraph />, onClick: () => handleNavigate("content_blocks") }
        : null,
      hasFeature("EL_SET_RLS")
        ? { key: "rules", label: "Rules", actionType: "custom" as const, icon: <Icon.Paragraph />, onClick: () => handleNavigate("rules") }
        : null,
    ].filter(Boolean) as NavigatorChildItem[];

    if (engagementItems.length > 0) {
      nav.push({
        key: "engagement-letter-setup",
        label: "EL Setup",
        icon: <Icon.GearWideConnected />,
        childrenOpenMode: "inline",
        children: [{ key: "engagement-letter-general", label: "General", items: engagementItems }],
      });
    }

    if (hasFeature("CLT_RECON")) {
      nav.push({
        key: "client_reconciliation",
        label: "Client Reconciliation",
        actionType: "internal",
        icon: <Icon.Intersect />,
      });
    }

    if (hasFeature("CDH")) {
      nav.push({
        key: "redirecttocdh",
        label: "Cygnus Datahub",
        actionType: "custom",
        icon: <Icon.ArrowUpRightSquareFill />,
        onClick: redirectToDatahub,
        isHidden: true,
      });
    }

    return nav;
  }, [handleNavigate, hasFeature, redirectToDatahub]);

  const settingsAction = useMemo<NavigatorBottomAction>(() => {
    const settingsChildren: NavigatorBottomAction["children"] = [];

    if (hasFeature("THMS")) {
      settingsChildren.push({
        key: "settings-general",
        label: "General",
        items: [
          {
            key: "appearance",
            label: "Appearance",
            actionType: "internal",
            icon: <Icon.PaletteFill />,
          },
        ],
      });
    }

    if (hasFeature("FRMADMN")) {
      settingsChildren.push({
        key: "settings-user",
        label: "User Settings",
        items: [
          {
            key: "firmadmin",
            label: "Firm Admin Panel",
            actionType: "internal",
            icon: <Icon.PersonFillGear />,
          },
        ],
      });
    }

    settingsChildren.push({
      key: "settings-misc",
      label: "Misc",
      stickToBottom: true,
      items: [
        {
          key: "clear-cache",
          label: "Clear Cache",
          actionType: "custom",
          icon: <Icon.ArrowClockwise />,
          onClick: () => {
            localStorage.clear();
            window.location.reload();
          },
        },
      ],
    });

    return {
      key: "settings",
      label: "Settings",
      icon: <Icon.GearFill />,
      childrenOpenMode: "panel",
      children: settingsChildren,
    };
  }, [hasFeature]);

  const secondaryBottomActions = useMemo<NavigatorBottomAction[]>(
    () =>
      hasFeature("CDH")
        ? [
            {
              key: "redirecttocdh",
              label: "Cygnus Datahub",
              actionType: "custom",
              icon: <Icon.ArrowUpRightSquareFill />,
              onClick: redirectToDatahub,
            },
          ]
        : [],
    [hasFeature, redirectToDatahub]
  );

  const activeParentKey = useMemo((): string => {
    const explicitMap: Record<string, string> = {
      client_dashboard: "dashboards",
      el_dashboard: "dashboards",
      el_dashboard_old: "dashboards",
      clients: "clients",
      client_approval: "client_approval",
      client_details: "client_details",
      approval_details: "approval_details",
      configure_fields: "configurations",
      field_mapping: "configurations",
      onboarding_forms: "configurations",
      onboarding_form_editor: "configurations",
      approval_config: "configurations",
      mappings: "configurations",
      coi_registry: "configurations",
      templates: "engagement-letter-setup",
      engagement_forms: "engagement-letter-setup",
      engagement_form_editor: "engagement-letter-setup",
      wizard_form_editor: "engagement-letter-setup",
      content_blocks: "engagement-letter-setup",
      rules: "engagement-letter-setup",
      chatgpt: "chatgpt",
      cygnus_gpt_dashboard: "chatgpt",
      client_reconciliation: "client_reconciliation",
    };

    return explicitMap[activeKey] || activeKey;
  }, [activeKey]);

  const renderContent = useCallback(
    (key: string): React.ReactNode => {
      switch (key) {
        case "clients":
          return <ClientsGrid transporter={handleNavigate} />;
        case "client_approval":
          return <ClientApprovalGrid transporter={handleNavigate} deepLink={deepLink} />;
        case "client_details":
          return <EngagementLetterDetailsPage transporter={handleNavigate} />;
        case "approval_details":
          return <ApprovalDetailsPage transporter={handleNavigate} deepLink={deepLink} />;
        case "client_dashboard":
          return <ClientDashboard transporter={handleNavigate} saveParamToApi={saveParamToApi} canViewExecutive={hasFeature("OP_EX_VIEW")}/>;
        case "el_dashboard":
          return <EngagementDashboard transporter={handleNavigate} saveParamToApi={saveParamToApi} canViewExecutive={hasFeature("OP_EX_VIEW")}/>;
        case "el_dashboard_old":
          return renderPage(EngagementDashboardOld, { transporter: handleNavigate });
        case "chatgpt":
        case "cygnus_gpt_dashboard":
          return <CygnusGptDashboard transporter={handleNavigate} />;
        case "logs":
          return renderPage(ClientDataMapping, { mapoption: pageOptions[key], transporter: handleNavigate });
        case "field_mapping":
          return renderPage(ClientDataMapping, { mapoption: pageOptions[key], transporter: handleNavigate });
        case "configure_fields":
          return renderPage(MDMFieldsConfig, { transporter: handleNavigate });
        case "onboarding_forms":
          return <IntakeFormsGrid transporter={handleNavigate} />;
        case "onboarding_form_editor":
          return renderPage(OnboardingFormConfig, {
            onboarding_form_config_option: pageOptions[key],
            transporter: handleNavigate,
          });
        case "engagement_forms":
          return <EngagementFormsGrid transporter={handleNavigate} />;
        case "engagement_form_editor":
          return renderPage(EngagementFormConfig, {
            engagement_form_config_option: pageOptions[key],
            transporter: handleNavigate,
          });
        case "wizard_form_editor":
          return <WizardFormConfig configoption={pageOptions[key]} transporter={handleNavigate} />;
        case "approval_config":
          return renderPage(ApprovalConfig, { transporter: handleNavigate });
        case "mappings":
          return renderPage(FieldMappings, { transporter: handleNavigate });
        case "coi_registry":
          return <COIRegistry />;
        case "coi_conflict_check":
          return renderPage(ConflictCheckResults, { pageOption: pageOptions[key], transporter: handleNavigate });
        case "appearance":
          return <ThemeSelector />;
        case "firmadmin":
          return <FirmAdminPage transporter={handleNavigate} />;
        case "templates":
          return <MyTemplatesPage transporter={handleNavigate} />;
        case "content_blocks":
          return renderPage(ContentBlocksPage, { transporter: handleNavigate });
        case "rules":
          return renderPage(RulesPage, { transporter: handleNavigate });
        case "client_reconciliation":
          return renderPage(DiffToolMainClientGridPage, { transporter: handleNavigate });
        default:
          return <div className="dashboard-empty-state">Select a page</div>;
      }
    },
    [deepLink, handleNavigate, pageOptions, saveParamToApi]
  );

  return (
    <CygnusAppNavigator
      brand={{
        brandName: "Cygnus Accounting Hub",
        brandLogo: <img className="sidenav-dashboard-logo" src={require("../../images/dashboard_logo.png")} alt="Cygnus" />,
        onBrandClick: () => navigate("/dashboard"),
      }}
      items={items}
      activeKey={activeKey}
      activeParentKey={activeParentKey}
      userCard={{
        variant: "compact",
        name: userName,
        subtitle: `${userType || ""} \u2022 ${firmName || ""}`,
      }}
      settingsAction={settingsAction}
      secondaryBottomActions={secondaryBottomActions}
      onNavigate={handleNavigate}
      onLogout={logout}
      renderContent={renderContent}
      shouldConfirmNavigation={(currentKey, nextKey) => currentKey === "dynamicform" && currentKey !== nextKey}
      confirmationLabels={{
        title: "Unsaved changes",
        body: "You have unsaved changes. Are you sure you want to leave this page?",
        confirmButtonText: "Skip and leave",
        cancelButtonText: "Stay and save",
      }}
      collapsedWidth={70}
      expandedWidth={250}
    />
  );
};

export default Dashboard;
